import '../CreateEditDialog.scss';

import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';

import { usePhoneOptionsContext } from '../../../../../../../context/PhoneOptionsContext';
import useAxiosHook from '../../../../../../../hooks/useAxiosHook';
import { WithData } from '../../../../../../../types/api';
import {
  MunicipalityCollection,
  MunicipalityPlaceCollection,
} from '../../../../../../../types/api/municipalities';
import { PlacesStreetCollection } from '../../../../../../../types/api/places';
import {
  debounceTimeout,
  isStreetFreeInputAllowed,
} from '../../../../../../../utils/constants/misc';
import { invalidPhoneNumberCharactersRegex } from '../../../../../../../utils/constants/phoneNumbers';
import { queryString } from '../../../../../../../utils/helpers/http';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../../../../utils/helpers/phoneNumbers';
import FieldWithErrors from '../../../../../../Forms/ReactHookForm/FieldWithErrors';

function GeneralInfo() {
  const { t } = useTranslation();

  const { getValues, setValue } = useFormContext();
  const [previewUrl, setPreviewUrl] = useState<any>();
  const municipalityId = useWatch({ name: 'address.municipalityId' });
  const placeId = useWatch({ name: 'address.placeId' });
  const streetId = useWatch({ name: 'address.streetId' });
  const partnerName = useWatch({ name: 'partnerName' });
  const branchManagerName = useWatch({ name: 'branchManagerName' });

  const debouncedPartnerName = useDebounce(partnerName, debounceTimeout);

  const debouncedBranchManagerName = useDebounce(
    branchManagerName,
    debounceTimeout
  );

  const { data: franchisesData, isLoading: isPartnerLoading } =
    useAxiosHook<any>(
      '/v2/franchises' +
        queryString({ ime: debouncedPartnerName, page: 1, limit: 10 })
    );

  const { data: employeesData, isLoading: isEmployeesLoading } =
    useAxiosHook<any>(
      '/employees' +
        queryString({
          'roles[]': 10,
          fullname: debouncedBranchManagerName,
          limit: 0,
        })
    );

  const { data: municipalities, isLoading: isMunicipalitiesLoading } =
    useAxiosHook<WithData<MunicipalityCollection>>('/municipalities?limit=0');

  const {
    data: places,
    reload: reloadPlaces,
    isLoading: isPlacesLoading,
  } = useAxiosHook<MunicipalityPlaceCollection>(
    `/municipalities/${municipalityId}/places?limit=0`,
    {
      skipWhen: !municipalityId,
      skipCache: true,
    }
  );

  const {
    data: streets,
    reload: reloadStreets,
    isLoading: isStreetsLoading,
  } = useAxiosHook<WithData<PlacesStreetCollection>>(
    `/places/${placeId}/streets?limit=0`,
    {
      skipWhen: !placeId,
      skipCache: true,
    }
  );

  const municipalitiesOptions = useMemo(
    () =>
      municipalities && municipalities.data.length > 0
        ? municipalities?.data?.map((m) => ({
            label: m.name,
            value: m.id,
          }))
        : [],
    [municipalities, municipalityId]
  );

  const placesOptions = useMemo(
    () =>
      places && places.length > 0
        ? places?.map((m) => ({
            label: m.place_name,
            value: m.place_id,
            postal_code: m.zip,
          }))
        : [],
    [places, municipalityId]
  );

  const streetsOptions = useMemo(
    () =>
      streets && streets?.data?.length > 0
        ? streets?.data?.map((m) => ({ label: m.name, value: m.id }))
        : [],
    [streets, placeId]
  );

  const franchisesOptions = useMemo(
    () =>
      franchisesData?.data?.map((m: any) => ({ label: m.name, value: m.id })) ??
      [],
    [franchisesData]
  );

  const employeesOptions = useMemo(
    () =>
      employeesData?.data?.map((client: any) => ({
        label: `${client.ime} ${client.prezime}`,
        value: client.id,
      })) ?? [],
    [employeesData]
  );

  useEffect(() => {
    if (!getValues()?.audio) {
      setPreviewUrl(null);
      return;
    }

    try {
      if (getValues().audio instanceof File) {
        const url = URL.createObjectURL(getValues().audio);
        setPreviewUrl(url);
      } else {
        const byteCharacters = atob(getValues()?.audio || '');
        const byteNumbers = new Array(byteCharacters.length)
          .fill(0)
          .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'audio/wav' });
        setPreviewUrl(URL.createObjectURL(blob));
      }
    } catch (e) {
      console.error('Invalid base64 audio:', getValues().audio);
      setPreviewUrl(null);
    }
  }, [getValues]);

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  const onSelect = (e: any) => {
    const f = e.files[0];
    if (f.type.startsWith('audio/')) {
      setValue('audio', f);
      setPreviewUrl(URL.createObjectURL(f));
    }
  };

  return (
    <div className="general-info-tab p-fluid">
      <div className="p-grid">
        <div className="p-col-12 p-md-6">
          <FieldWithErrors name="name" label={t('Name')}>
            <Controller
              name="name"
              render={({ field }) => (
                <InputText
                  id="name"
                  name="name"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="code" label={t('Code')}>
            <Controller
              name="code"
              render={({ field }) => (
                <InputText
                  id="code"
                  name="code"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors
            name="address.municipalityId"
            label={t('Municipality')}
          >
            <Controller
              name="address.municipalityId"
              render={({ field }) => (
                <Dropdown
                  id="address.municipalityId"
                  name="address.municipalityId"
                  options={municipalitiesOptions}
                  value={field.value}
                  onChange={(e) => {
                    if (e.value) {
                      field.onChange(e.value);
                      const items = municipalitiesOptions.find(
                        (s) => s.value === e.value
                      );
                      setValue('address.municipalityName', items?.label || '');

                      reloadPlaces({
                        url: `municipalities/${e.value}/places?limit=0`,
                      });

                      setValue('address.placeId', null);
                      setValue('address.placeName', '');
                      setValue('address.streetId', null);
                      setValue('address.streetName', '');
                      setValue('address.housenumber', '');
                      setValue('address.entrance', '');
                      setValue('address.apartment', '');
                    }
                  }}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isMunicipalitiesLoading ? t('Loading...') : ''}
                  className="data-cy-municipalityId"
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="address.placeId" label={t('Place')}>
            <Controller
              name="address.placeId"
              render={({ field }) => (
                <Dropdown
                  id="address.placeId"
                  name="address.placeId"
                  options={placesOptions}
                  value={field.value}
                  onChange={(e) => {
                    if (e.value) {
                      field.onChange(e.value);
                      const items = placesOptions.find(
                        (s) => s.value === e.value
                      );
                      setValue('address.placeName', items?.label || '');

                      reloadStreets({
                        url: `places/${e.value}/streets?limit=0`,
                      });
                    } else {
                      setValue('address.placeId', null);
                      setValue('address.placeName', '');
                      setValue('address.streetId', null);
                      setValue('address.streetName', '');
                      setValue('address.housenumber', '');
                      setValue('address.entrance', '');
                      setValue('address.apartment', '');
                    }
                  }}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isPlacesLoading ? t('Loading...') : ''}
                  disabled={!municipalityId || isPlacesLoading}
                  className="data-cy-placeId"
                />
              )}
            />
          </FieldWithErrors>

          {isStreetFreeInputAllowed ? (
            <FieldWithErrors name="address.rawName" label={t('Address')}>
              <Controller
                name="address.rawName"
                render={({ field }) => (
                  <InputText
                    id="address.rawName"
                    name="address.rawName"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!municipalityId || !placeId}
                    max={256}
                  />
                )}
              />
            </FieldWithErrors>
          ) : (
            <>
              <FieldWithErrors name="address.streetId" label={t('Street')}>
                <Controller
                  name="address.streetId"
                  render={({ field }) => (
                    <Dropdown
                      id="address.streetId"
                      name="address.streetId"
                      options={streetsOptions}
                      value={field.value}
                      onChange={(e) => {
                        if (e.value) {
                          field.onChange(e.value);
                          const items = streetsOptions.find(
                            (s) => s.value === e.value
                          );
                          setValue('address.streetName', items?.label || '');
                        } else {
                          setValue('address.streetId', null);
                          setValue('address.streetName', '');
                          setValue('address.housenumber', '');
                          setValue('address.entrance', '');
                          setValue('address.apartment', '');
                        }
                      }}
                      filter
                      filterPlaceholder={t('Search')}
                      placeholder={isStreetsLoading ? t('Loading...') : ''}
                      disabled={!placeId || isStreetsLoading}
                      className="data-cy-streetId"
                    />
                  )}
                />
              </FieldWithErrors>

              <div className="street-numbers">
                <FieldWithErrors
                  name="address.housenumber"
                  label={t('Street No.')}
                >
                  <Controller
                    name="address.housenumber"
                    render={({ field }) => (
                      <InputText
                        id="address.housenumber"
                        name="address.housenumber"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!streetId}
                      />
                    )}
                  />
                </FieldWithErrors>

                <span className="divider">/</span>

                <FieldWithErrors
                  name="address.entrance"
                  label={t('Entrance No.')}
                >
                  <Controller
                    name="address.entrance"
                    render={({ field }) => (
                      <InputText
                        id="address.entrance"
                        name="address.entrance"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!streetId}
                      />
                    )}
                  />
                </FieldWithErrors>

                <span className="divider">/</span>

                <FieldWithErrors name="address.apartment" label={t('Flat No.')}>
                  <Controller
                    name="address.apartment"
                    render={({ field }) => (
                      <InputText
                        id="address.apartment"
                        name="address.apartment"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!streetId}
                      />
                    )}
                  />
                </FieldWithErrors>
              </div>
            </>
          )}

          <div>
            <FileUpload
              name="audio"
              accept="audio/wav"
              maxFileSize={20000000}
              onSelect={onSelect}
              chooseLabel={t('Choose Audio')}
              auto={false}
              mode="basic"
              onClear={() => {
                setValue('audio', null);
                setPreviewUrl(null);
              }}
              onRemove={() => {
                setValue('audio', null);
                setPreviewUrl(null);
              }}
            />

            {previewUrl && (
              <div style={{ marginTop: '1rem' }}>
                <audio controls src={previewUrl} style={{ width: '100%' }} />
              </div>
            )}
          </div>
        </div>

        <div className="p-col-12 p-md-6">
          <span className="dialog-section-divider">{t('Contact Info')}</span>
          <FieldWithErrors name="mobile" label={t('Mobile')}>
            <div className="p-inputgroup number-group">
              <Controller
                name="mobile_region"
                render={({ field }) => (
                  <Dropdown
                    style={{ padding: 0, width: '5rem' }}
                    id="mobile_region"
                    options={languages}
                    itemTemplate={countryOptionTemplate}
                    valueTemplate={selectedCountryTemplate}
                    data-cy="mobile_region"
                    {...field}
                  />
                )}
              />
              <Controller
                name="mobile"
                render={({ field }) => (
                  <InputText
                    name="mobile"
                    style={{ width: '100%' }}
                    value={formatMobileNumber(
                      getValues()?.mobile,
                      getValues()?.mobile_region ||
                        getPhoneorMobileNumberRegion(getValues()?.mobile)
                    )}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value?.replace(
                          invalidPhoneNumberCharactersRegex,
                          ''
                        )
                      );
                    }}
                    className="data-cy-mobile"
                  />
                )}
              />
            </div>
          </FieldWithErrors>

          <FieldWithErrors name="phone" label={t('Phone')}>
            <div className="p-inputgroup">
              <Controller
                name="phone_region"
                render={({ field }) => (
                  <Dropdown
                    style={{ padding: 0, width: '5rem' }}
                    id="phone_region"
                    options={languages}
                    itemTemplate={countryOptionTemplate}
                    valueTemplate={selectedCountryTemplate}
                    data-cy="phone_region"
                    {...field}
                  />
                )}
              />
              <Controller
                name="phone"
                render={({ field }) => (
                  <InputText
                    name="phone"
                    style={{ width: '100%' }}
                    value={formatPhoneNumber(
                      getValues()?.phone,
                      getValues()?.phone_region ||
                        getPhoneorMobileNumberRegion(getValues()?.phone)
                    )}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value?.replace(
                          invalidPhoneNumberCharactersRegex,
                          ''
                        )
                      );
                    }}
                    className="data-cy-telefon"
                  />
                )}
              />
            </div>
          </FieldWithErrors>

          <FieldWithErrors name="email" label={t('Email')}>
            <Controller
              name="email"
              render={({ field }) => (
                <InputText
                  id="email"
                  name="email"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <span className="dialog-section-divider">
            {t('Regions and Franchise')}
          </span>

          <FieldWithErrors name="franchiseId" label={t('Franchise')}>
            <Controller
              name="franchiseId"
              render={({ field }) => (
                <Dropdown
                  id="franchiseId"
                  name="franchiseId"
                  options={franchisesOptions}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isPartnerLoading ? t('Loading...') : ''}
                  disabled={isPartnerLoading}
                  className="data-cy-franchiseId"
                />
              )}
            />
          </FieldWithErrors>

          {/* <FieldWithErrors name="regions" label={t('Regions')}>
            <Controller
              name="regions"
              render={({ field }) => (
                <MultiSelect
                  id="regions"
                  name="regions"
                  optionLabel="name"
                  optionValue="id"
                  options={regionsOptions}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isLoadingRegions ? t('Loading...') : ''}
                  disabled={isPartnerLoading}
                  className="data-cy-regions"
                />
              )}
            />
          </FieldWithErrors> */}

          <FieldWithErrors name="branchManagerId" label={t('Branch Manager')}>
            <Controller
              name="branchManagerId"
              render={({ field }) => (
                <Dropdown
                  id="branchManagerId"
                  name="branchManagerId"
                  options={employeesOptions}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isEmployeesLoading ? t('Loading...') : ''}
                  disabled={isEmployeesLoading}
                  className="data-cy-branchManagerId"
                />
              )}
            />
          </FieldWithErrors>

          <div className="sorting-center p-field-checkbox">
            <FieldWithErrors
              name="sortingCenter"
              label={false}
              className="p-d-flex p-mb-2 p-jc-between"
            >
              <Controller
                name="sortingCenter"
                render={({ field }) => (
                  <>
                    <Checkbox
                      id="sortingCenter"
                      name="sortingCenter"
                      value={field.value}
                      checked={field.value}
                      onChange={field.onChange}
                    />
                    <label htmlFor="sortingCenter" data-cy="sortingCenter">
                      {t('Warehouse is a sorting center')}
                    </label>
                  </>
                )}
              />
            </FieldWithErrors>
          </div>

          <div className="drop-point p-field-checkbox">
            <FieldWithErrors
              name="dropPoint"
              label={false}
              className="p-d-flex p-mb-2 p-jc-between"
            >
              <Controller
                name="dropPoint"
                render={({ field }) => (
                  <>
                    <Checkbox
                      id="dropPoint"
                      name="dropPoint"
                      value={field.value}
                      checked={field.value}
                      onChange={field.onChange}
                    />
                    <label htmlFor="dropPoint" data-cy="dropPoint">
                      {t(
                        'Parcels can be delivered or picked up from warehouse'
                      )}
                    </label>
                  </>
                )}
              />
            </FieldWithErrors>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GeneralInfo;
